/**
 * Reading structured errors back out of `apiRequest`.
 *
 * `throwIfResNotOk` (lib/queryClient.ts) collapses any non-2xx into
 * `Error("<status>: <raw body text>")`. That is fine for logging and useless for
 * branching: a caller that wants to react to a specific server `reason` has only
 * a string. Every `onError` handler then shows the user the raw JSON.
 *
 * These helpers parse that string back into a status + body so call sites can
 * branch on the server's machine-readable `reason` and show real UI.
 */

/** The server's reason code when a Basic account is at its friend cap. */
export const FRIEND_LIMIT_REASON = "friend_limit_reached";

export interface ParsedApiError {
  status: number | null;
  body: Record<string, unknown> | null;
  /** The server's human-readable message, when it sent one. */
  message: string | null;
}

/** Pull the status and JSON body out of an apiRequest error. */
export function parseApiError(err: unknown): ParsedApiError {
  const raw = err instanceof Error ? err.message : String(err ?? "");
  const m = /^(\d{3}):\s*([\s\S]*)$/.exec(raw);
  if (!m) return { status: null, body: null, message: null };
  const status = Number(m[1]);
  try {
    const parsed: unknown = JSON.parse(m[2]);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const body = parsed as Record<string, unknown>;
      const message = typeof body.message === "string" ? body.message : null;
      return { status, body, message };
    }
  } catch {
    /* not JSON — fall through to a status-only result */
  }
  return { status, body: null, message: null };
}

/** True when the server refused because the actor is at the Basic friend cap. */
export function isFriendLimitError(err: unknown): boolean {
  const { status, body } = parseApiError(err);
  return status === 403 && body?.reason === FRIEND_LIMIT_REASON;
}

/**
 * What to show the user for a failed request: the server's own message when it
 * sent a structured one, otherwise the raw error text.
 *
 * Without this, a 403 renders as `403: {"message":"…","reason":"…"}`.
 */
export function errorMessage(err: unknown): string {
  const { message } = parseApiError(err);
  if (message) return message;
  return err instanceof Error ? err.message : String(err ?? "Something went wrong");
}

/**
 * The sentence to show for a failed AuthProvider call (sign in, sign up, save
 * the trainer step).
 *
 * AuthProvider throws the RAW response body (`{"message":"..."}`), where
 * `apiRequest` throws `"<status>: <body>"`; this reads either. Four call sites
 * each carried their own copy of this parse, and the one that was missed (the
 * sign-up page's "this email already has an account, try signing in" fallback)
 * printed `{"message":"Invalid email or password"}` on the form, braces and
 * all (device pass, 2026-10-02). Use this instead of `err.message`.
 */
export function authErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!raw.trim()) return fallback;
  const brace = raw.indexOf("{");
  if (brace >= 0) {
    try {
      const body: unknown = JSON.parse(raw.slice(brace));
      if (body && typeof body === "object" && !Array.isArray(body)) {
        const message = (body as Record<string, unknown>).message;
        if (typeof message === "string" && message.trim()) return message;
        // JSON with no message is still not something to show a trainer.
        return fallback;
      }
    } catch {
      /* not JSON: fall through to the text itself */
    }
  }
  return raw;
}
